import type { LucideIcon } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { cn } from "@/shared/lib/utils";
import { Spinner } from "./spinner";

/**
 * Variantes:
 *  - primary    ação principal da tela/diálogo (1 por contexto)
 *  - secondary  ações secundárias (borda)
 *  - ghost      ações terciárias / barras de ferramenta
 *  - danger     ação destrutiva confirmada (excluir, desligar)
 *  - danger-ghost destrutiva de baixa ênfase (link "Excluir" num cabeçalho)
 *  - link       texto com aparência de link
 * Aliases legados: default → primary, outline → secondary, destructive → danger.
 */
export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "danger-ghost" | "link" | "default" | "outline" | "destructive";
export type ButtonSize = "sm" | "md" | "lg";

const variants: Record<ButtonVariant, string> = {
  primary: "bg-primary text-primary-fg shadow-xs hover:bg-primary-hover",
  secondary: "border border-border-strong bg-surface text-fg shadow-xs hover:bg-surface-hover",
  ghost: "text-fg-muted hover:bg-surface-hover hover:text-fg",
  danger: "bg-danger text-on-danger shadow-xs hover:bg-danger-hover",
  "danger-ghost": "text-danger-fg hover:bg-danger-subtle",
  link: "h-auto px-0 text-brand-accent underline-offset-4 hover:underline",
  default: "",
  outline: "",
  destructive: "",
};
variants.default = variants.primary;
variants.outline = variants.secondary;
variants.destructive = variants.danger;

const sizes: Record<ButtonSize, string> = {
  sm: "h-8 gap-1.5 rounded-md px-3 text-body-sm",
  md: "h-9 gap-2 rounded-md px-3.5 text-body",
  lg: "h-11 gap-2 rounded-md px-5 text-body",
};

const iconSizes: Record<ButtonSize, string> = { sm: "h-3.5 w-3.5", md: "h-4 w-4", lg: "h-4.5 w-4.5" };

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Ícone à esquerda (Lucide). */
  icon?: LucideIcon;
  /** Ícone à direita (ex.: ChevronDown em gatilhos de menu). */
  iconRight?: LucideIcon;
  /** Ocupa a largura toda. */
  block?: boolean;
  ref?: Ref<HTMLButtonElement>;
};

export function Button({
  className,
  variant = "primary",
  size = "md",
  loading,
  icon: Icon,
  iconRight: IconRight,
  block,
  children,
  disabled,
  type = "button",
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        "inline-flex select-none items-center justify-center whitespace-nowrap font-medium transition-colors duration-150",
        "disabled:pointer-events-none disabled:opacity-50",
        sizes[size],
        variants[variant],
        block && "w-full",
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner className={iconSizes[size]} /> : Icon ? <Icon className={cn("shrink-0", iconSizes[size])} strokeWidth={2} aria-hidden /> : null}
      {children as ReactNode}
      {IconRight && !loading ? <IconRight className={cn("shrink-0", iconSizes[size])} strokeWidth={2} aria-hidden /> : null}
    </button>
  );
}
